import React, { useState, useEffect, useRef } from "react";
import Icon from "../../../components/AppIcon";

const CategoryTabs = ({ categories, activeCategory, onCategoryChange }) => {
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });
  const tabRefs = useRef({});
  const containerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Update the underline position when active tab changes or on mount
  useEffect(() => {
    const activeTab = tabRefs.current[activeCategory];
    if (activeTab) {
      setSliderStyle({
        left: activeTab.offsetLeft,
        width: activeTab.offsetWidth,
      });
    }
  }, [activeCategory]);

  // Check scroll position to show/hide arrows (mobile only)
  const checkScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, []);

  const scroll = (direction) => {
    if (containerRef.current) {
      const scrollAmount = 200;
      containerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="relative">
      {/* Desktop Tabs */}
      <div className="hidden md:flex border-b border-slate-200 mb-4">
        <div className="flex space-x-1">
          {categories.map((category) => (
            <button
              key={category.id}
              ref={(el) => (tabRefs.current[category.id] = el)}
              onClick={() => onCategoryChange(category.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${activeCategory === category.id
                ? "bg-white text-primary-600"
                : "text-slate-600 hover:text-primary-600 hover:bg-slate-50"
                }`}
              aria-current={activeCategory === category.id ? "page" : undefined}
            >
              {category.name}
            </button>
          ))}
        </div>
        {/* Underline animation */}
        <div
          className="absolute bottom-0 h-1 bg-primary-600 transition-all duration-300"
          style={sliderStyle}
        />
      </div>

      {/* Mobile Scrollable Tabs with Arrows */}
      <div className="md:hidden mb-6 relative">
        {/* Left Scroll Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-1 top-0 bottom-0 my-auto z-30 w-9 h-9 rounded-full bg-white shadow-xl border-2 border-blue-200 flex items-center justify-center text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-all duration-300"
            aria-label="Scroll left"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>
        )}

        {/* Right Scroll Arrow */}
        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-1 top-0 bottom-0 my-auto z-30 w-9 h-9 rounded-full bg-white shadow-xl border-2 border-blue-200 flex items-center justify-center text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-all duration-300"
            aria-label="Scroll right"
          >
            <Icon name="ChevronRight" size={20} />
          </button>
        )}

        {/* Left Gradient Fade */}
        <div
          className={`absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white to-transparent z-20 pointer-events-none transition-opacity duration-300 ${showLeftArrow ? 'opacity-100' : 'opacity-0'
            }`}
        />

        {/* Right Gradient Fade */}
        <div
          className={`absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent z-20 pointer-events-none transition-opacity duration-300 ${showRightArrow ? 'opacity-100' : 'opacity-0'
            }`}
        />

        {/* Scrollable Tab Container */}
        <div
          ref={containerRef}
          className="flex gap-3 overflow-x-auto py-2 scroll-smooth items-center"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            paddingLeft: '2px',
            paddingRight: '2px'
          }}
        >
          {categories.map((category) => {
            const isSelected = activeCategory === category.id;

            return (
              <button
                key={category.id}
                onClick={() => onCategoryChange(category.id)}
                className={`relative flex-shrink-0 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center gap-2 shadow-sm hover:shadow-lg overflow-hidden ${isSelected
                    ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg"
                    : "bg-white text-slate-700 border-2 border-slate-200 hover:border-blue-300 hover:bg-blue-50"
                  }`}
                aria-pressed={isSelected}
              >
                {/* Icon and Text */}
                <span className="relative z-10 flex items-center gap-2 whitespace-nowrap">
                  {category.name}
                </span>

                {/* Selection Indicator - Bottom Bar */}
                {isSelected && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 to-pink-400 rounded-b-xl" />
                )}
              </button>
            );
          })}
        </div>

        {/* Hide scrollbar */}
        <style jsx>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>
    </div>
  );
};

export default CategoryTabs;
